import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ForkliftService } from '../../../service/api/forklift-service';
import { redux } from 'Core/redux';

export const STORE_KEY = 'forklift';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  maintenanceRecordsData: [],
  maintenanceRecordData: null,
  maintenanceData: null,
  cellHistoryData: [],
  tileView: true,
};

// Fetch all forklifts
export const fetchForklifts = createAsyncThunk(
  'forklift/fetchForklifts',
  async () => {
    const { response } = await ForkliftService.getAllForklifts();
    return response;
  }
);

// Fetch forklifts with filters
export const fetchForkliftsWithFilters = createAsyncThunk(
  'forklift/fetchForkliftsWithFilters',
  async (filters = {}) => {
    const { response } = await ForkliftService.getAllForklifts(filters);
    return response;
  }
);

// Save forklift
export const saveForklift = createAsyncThunk(
  'forklift/saveForklift',
  async (data) => {
    const { response } = await ForkliftService.saveForklift(data);
    return response;
  }
);

// Update forklift
export const updateForklift = createAsyncThunk(
  'forklift/updateForklift',
  async (data) => {
    const { response } = await ForkliftService.updateForklift(data);
    return response;
  }
);

// Add forklift (alias for save)
export const addForklift = createAsyncThunk(
  'forklift/addForklift',
  async (data) => {
    const { response } = await ForkliftService.saveNewForklift(data);
    return response;
  }
);

// Edit forklift (alias for update)
export const editForklift = createAsyncThunk(
  'forklift/editForklift',
  async (data) => {
    const { response } = await ForkliftService.updateForklift(data);
    return response;
  }
);

// Delete forklift
export const deleteForklift = createAsyncThunk(
  'forklift/deleteForklift',
  async (id) => {
    const { response } = await ForkliftService.deleteForklift(id);
    return response;
  }
);

// Save forklift maintenance
export const saveForkliftMaintenance = createAsyncThunk(
  'forklift/saveForkliftMaintenance',
  async (data) => {
    const { response } = await ForkliftService.saveForkliftMaintenance(data);
    return response;
  }
);

// Fetch maintenance records by forklift ID
export const fetchMaintenanceRecordsByForkliftId = createAsyncThunk(
  'forklift/fetchMaintenanceRecordsByForkliftId',
  async (forkliftId) => {
    const { response } = await ForkliftService.getMaintenanceRecordsByForkliftId(forkliftId);
    return response;
  }
);

// Fetch forklift by asset number for cell history
export const fetchForkliftByAssetNumber = createAsyncThunk(
  'forklift/fetchByAssetNumber',
  async (assetNumber) => {
    const { response } = await ForkliftService.getAllForkliftByAssetNumber(assetNumber);
    return response;
  }
);

// Fetch maintenance record details
export const fetchMaintenanceRecordsDetails = createAsyncThunk(
  'forklift/fetchMaintenanceRecordsDetails',
  async (recordId) => {
    const { response } = await ForkliftService.getMaintenanceRecordDetails(recordId);
    return response;
  }
);

// Add forklift maintenance record
export const addForkliftMaintenanceRecord = createAsyncThunk(
  'forklift/addForkliftMaintenanceRecord',
  async (data) => {
    const { response } = await ForkliftService.addMaintenanceRecord(data);
    return response;
  }
);

export const { reducer, actions } = createSlice({
  name: STORE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setForklift: (state, action) => {
      // Ensure data is always an array
      if (Array.isArray(action.payload)) {
        state.data = action.payload;
      } else if (action.payload && typeof action.payload === 'object') {
        // If payload is a single object, wrap it in an array
        state.data = [action.payload];
      } else {
        state.data = [];
      }
    },
    setMaintenanceData: (state, action) => {
      state.maintenanceData = action.payload;
    },
    clearMaintenanceData: (state) => {
      state.maintenanceData = null;
    },
    resetFilters: (state) => {
      // Reset any filter-related state if needed
      state.loading = false;
      state.error = null;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch forklifts
      .addCase(fetchForklifts.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchForklifts.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure data is always an array
        if (Array.isArray(action.payload)) {
          state.data = action.payload;
        } else if (action.payload?.data && Array.isArray(action.payload.data)) {
          state.data = action.payload.data;
        } else if (action.payload && typeof action.payload === 'object') {
          // If payload is a single object, wrap it in an array
          state.data = [action.payload];
        } else {
          state.data = [];
        }
      })
      .addCase(fetchForklifts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch forklifts with filters
      .addCase(fetchForkliftsWithFilters.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchForkliftsWithFilters.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure data is always an array
        if (Array.isArray(action.payload)) {
          state.data = action.payload;
        } else if (action.payload?.data && Array.isArray(action.payload.data)) {
          state.data = action.payload.data;
        } else if (action.payload && typeof action.payload === 'object') {
          // If payload is a single object, wrap it in an array
          state.data = [action.payload];
        } else {
          state.data = [];
        }
      })
      .addCase(fetchForkliftsWithFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Save forklift
      .addCase(saveForklift.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveForklift.fulfilled, (state, action) => {
        state.loading = false;
        state.data = [...state.data, action.payload];
      })
      .addCase(saveForklift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update forklift
      .addCase(updateForklift.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateForklift.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.data.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(updateForklift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add forklift
      .addCase(addForklift.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addForklift.fulfilled, (state, action) => {
        state.loading = false;
        state.data = [...state.data, action.payload];
      })
      .addCase(addForklift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Edit forklift
      .addCase(editForklift.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editForklift.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.data.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editForklift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Delete forklift
      .addCase(deleteForklift.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteForklift.fulfilled, (state, action) => {
        state.loading = false;
        state.data = state.data.filter(item => item.id !== action.payload);
      })
      .addCase(deleteForklift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Save forklift maintenance
      .addCase(saveForkliftMaintenance.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveForkliftMaintenance.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceData = action.payload;
      })
      .addCase(saveForkliftMaintenance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch maintenance records by forklift ID
      .addCase(fetchMaintenanceRecordsByForkliftId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsByForkliftId.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordsData = action.payload;
      })
      .addCase(fetchMaintenanceRecordsByForkliftId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch maintenance record details
      .addCase(fetchMaintenanceRecordsDetails.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordData = action.payload;
      })
      .addCase(fetchMaintenanceRecordsDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add forklift maintenance record
      .addCase(addForkliftMaintenanceRecord.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addForkliftMaintenanceRecord.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordsData = [...state.maintenanceRecordsData, action.payload];
      })
      .addCase(addForkliftMaintenanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch forklift by asset number (cell history)
      .addCase(fetchForkliftByAssetNumber.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchForkliftByAssetNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.cellHistoryData = action.payload;
      })
      .addCase(fetchForkliftByAssetNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

redux.injectReducer(STORE_KEY, reducer);